// pages/Escalation/EscalationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, CheckCircle, MessageSquare, User, 
  Clock, ChevronRight, Users, Sparkles, Bell,
  Send, X, Loader2
} from 'lucide-react';
import './EscalationPage.css';

const EscalationPage = () => {
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    fetchEscalations();
    // Set interval to refresh every 10 seconds
    const interval = setInterval(fetchEscalations, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchEscalations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/inbox/conversations/escalated');
      setEscalations(response.data);
    } catch (err) {
      console.error('Error fetching escalations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (conversationId) => {
    if (!replyText.trim()) return;
    
    setReplying(true);
    setReplyingTo(conversationId);
    setError('');
    setMessage('');

    try {
      // Send reply
      await api.post(`/api/inbox/conversations/${conversationId}/reply`, {
        content: replyText.trim()
      });

      // Resolve escalation after replying
      await api.put(`/api/inbox/conversations/${conversationId}/resolve-escalation`);

      setMessage('✅ Reply sent and escalation resolved!');
      
      // Remove from list
      setEscalations(prev => prev.filter(c => c.id !== conversationId));
      setReplyText('');
      setReplyingTo(null);

      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
      
    } catch (err) {
      setError('❌ Failed to send reply. Please try again.');
      console.error('Error replying:', err);
    } finally {
      setReplying(false);
    }
  };

  const handleResolveOnly = async (conversationId) => {
    setResolving(true);
    try {
      await api.put(`/api/inbox/conversations/${conversationId}/resolve-escalation`);
      setEscalations(prev => prev.filter(c => c.id !== conversationId));
      setMessage('✅ Escalation resolved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('❌ Failed to resolve escalation');
    } finally {
      setResolving(false);
    }
  };

  const handleOpenInbox = (conversationId) => {
    navigate(`/inbox?conversation=${conversationId}`);
  };

  // Auto focus input when replying
  useEffect(() => {
    if (replyingTo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyingTo]);

  if (loading) {
    return (
      <div className="escalation-loading">
        <div className="escalation-spinner"></div>
        <p>Loading escalated conversations...</p>
      </div>
    );
  }

  return (
    <div className="escalation-page">
      {/* Header */}
      <div className="escalation-header">
        <div>
          <h1 className="escalation-title">
            <Users size={28} /> Escalated Conversations
          </h1>
          <p className="escalation-subtitle">
            These customers need human assistance - reply directly here
          </p>
        </div>
        <div className="escalation-count">
          <Bell size={16} />
          <span>{escalations.length} pending</span>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="escalation-toast success">
          <CheckCircle size={16} />
          <span>{message}</span>
          <button onClick={() => setMessage('')}><X size={14} /></button>
        </div>
      )}
      {error && (
        <div className="escalation-toast error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      {/* No Escalations */}
      {escalations.length === 0 ? (
        <div className="escalation-empty">
          <CheckCircle size={48} />
          <h3>All clear! 🎉</h3>
          <p>No conversations need human attention right now</p>
        </div>
      ) : (
        <div className="escalation-list">
          {escalations.map(conv => {
            const isReplying = replyingTo === conv.id;
            
            return (
              <div key={conv.id} className="escalation-item">
                {/* Contact Info */}
                <div className="escalation-contact">
                  <div className="escalation-avatar">
                    {conv.contact?.name?.charAt(0) || '?'}
                  </div>
                  <div className="escalation-info">
                    <div className="escalation-name">
                      {conv.contact?.name || conv.contact?.phoneNumber || 'Unknown'}
                    </div>
                    <div className="escalation-phone">
                      {conv.contact?.phoneNumber}
                    </div>
                  </div>
                </div>

                {/* Escalation Reason */}
                <div className="escalation-reason">
                  <AlertCircle size={14} className="escalation-reason-icon" />
                  <span>{conv.escalationReason || 'AI could not handle the query'}</span>
                </div>

                {/* Last Message */}
                <div className="escalation-last-message">
                  <MessageSquare size={14} />
                  <span>{conv.lastMessage || 'No messages yet'}</span>
                </div>

                {/* Time */}
                <div className="escalation-time">
                  <Clock size={14} />
                  <span>
                    {conv.escalatedAt 
                      ? new Date(conv.escalatedAt).toLocaleString() 
                      : 'Recently'}
                  </span>
                </div>

                {/* ✅ NEW: Reply Section */}
                {isReplying ? (
                  <div className="escalation-reply-area">
                    <textarea
                      ref={inputRef}
                      className="escalation-reply-input"
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleReply(conv.id);
                        }
                      }}
                    />
                    <div className="escalation-reply-actions">
                      <button
                        className="escalation-cancel-btn"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="escalation-send-btn"
                        onClick={() => handleReply(conv.id)}
                        disabled={!replyText.trim() || replying}
                      >
                        {replying ? (
                          <><Loader2 size={16} className="spinning" /> Sending...</>
                        ) : (
                          <><Send size={16} /> Send & Resolve</>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="escalation-actions">
                    <button 
                      className="escalation-reply-btn"
                      onClick={() => setReplyingTo(conv.id)}
                    >
                      <MessageSquare size={16} />
                      Reply
                    </button>
                    <button 
                      className="escalation-chat-btn"
                      onClick={() => handleOpenInbox(conv.id)}
                      title="Open in Inbox"
                    >
                      <Users size={16} />
                      Inbox
                    </button>
                    <button 
                      className="escalation-resolve-btn"
                      onClick={() => handleResolveOnly(conv.id)}
                      disabled={resolving}
                    >
                      <CheckCircle size={16} />
                      Resolve
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EscalationPage;